import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import axios from "axios";
import getTags from "../../api/getTags";
import { TTag } from "types/tag";
import { IEmailImage } from "../../new-campaign/index";

export const useNewCampaignForm = () => {
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const isEditing = Boolean(id);

  const [campaignName, setCampaignName] = useState("");
  const [subject, setSubject] = useState("");
  const [emailContent, setEmailContent] = useState("");
  const [sendDate, setSendDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; subject?: string; emailContent?: string }>({});
  const [touched, setTouched] = useState<{ name: boolean; subject: boolean; emailContent: boolean }>({
    name: false,
    subject: false,
    emailContent: false,
  });

  const [isFormValid, setIsFormValid] = useState(false);
  const [tags, setTags] = useState<TTag[]>([]);

  useEffect(() => {
    getTags().then(setTags);
  }, []);

  // Recipient filtering state (Include & Exclude)
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [includeZipCodes, setIncludeZipCodes] = useState<number[]>([]);
  const [excludeZipCodes, setExcludeZipCodes] = useState<number[]>([]);
  const [includeCities, setIncludeCities] = useState<string[]>([]);
  const [excludeCities, setExcludeCities] = useState<string[]>([]);
  const [includeStates, setIncludeStates] = useState<string[]>([]);
  const [excludeStates, setExcludeStates] = useState<string[]>([]);
  const [includeTags, setIncludeTags] = useState<number[]>([]);
  const [excludeTags, setExcludeTags] = useState<number[]>([]);

  useEffect(() => {
    if (isEditing) {
      axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/outreach/email-campaign/${id}`)
        .then(({ data }) => {
          setCampaignName(data.name);
          setSubject(data.subject);
          setEmailContent(data.body); // ✅ Now contains <img src="S3_URL" />
          setSendDate(data.send_date || "");
          setIncludeZipCodes(data.include_zip_codes || []);
          setExcludeZipCodes(data.exclude_zip_codes || []);
          setIncludeCities(data.include_cities || []);
          setExcludeCities(data.exclude_cities || []);
          setIncludeStates(data.include_states || []);
          setExcludeStates(data.exclude_states || []);
          setIncludeTags(data.include_tags || []);
          setExcludeTags(data.exclude_tags || []);
        })
        .catch(error => console.error("Error fetching campaign:", error));
    }
  }, [id, isEditing]);

  // Validation function
  const validateForm = () => {
    let newErrors: { name?: string; subject?: string; emailContent?: string } = {};
    if (!campaignName.trim()) newErrors.name = "Campaign name is required.";
    if (!subject.trim()) newErrors.subject = "Subject is required.";
    if (!emailContent.trim()) newErrors.emailContent = "Email content cannot be empty.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    setIsFormValid(validateForm());
  }, [campaignName, subject, emailContent]);

  const handleSubmit = async (imageData: IEmailImage[]) => {
    if (!validateForm()) return;



    // Replace image urls with placeholders in emailContent
    let emailContentWithPlaceholders = emailContent;


    // Find all image URLs in the email content
    const imageUrls = emailContent.match(/src="([^"]+)"/g)?.map(src => src.replace(/src="|"/g, "")) || [];



    // Loop over found images, and replace them with placeholders by looking at the imageData array
    imageUrls.forEach((url) => {
      // Extract the filename from the URL
      const fileNameMatch = url.match(/\/([^\/?#]+)\?/); // Grabs 'image-1.png' from the signed URL
      let fileName = fileNameMatch?.[1];


      // Remove the file extension
      fileName = fileName?.replace(/\.[^/.]+$/, "");

      const transformedPlaceholder = fileName?.replace(/\W/g, "_"); // Transform the file name to match the placeholder format


      if (fileName) {
        // Try to find matching image data
        const image = imageData.find(img => (img.url && fileName && img.url.includes(fileName)) || (img.placeholder && fileName && img.placeholder.includes(fileName.replace(/\W/g, "_"))));

        if (url && transformedPlaceholder) {

          emailContentWithPlaceholders = emailContentWithPlaceholders.replace(url, transformedPlaceholder);
        }
      }
    });


    const campaignData = {
      name: campaignName,
      subject,
      emailContent: emailContentWithPlaceholders,
      sendDate: sendDate || null,
      filters: {
        includeZipCodes: includeZipCodes.length ? includeZipCodes : null,
        excludeZipCodes: excludeZipCodes.length ? excludeZipCodes : null,
        includeCities: includeCities.length ? includeCities : null,
        excludeCities: excludeCities.length ? excludeCities : null,
        includeStates: includeStates.length ? includeStates : null,
        excludeStates: excludeStates.length ? excludeStates : null,
        includeTags: includeTags.length ? includeTags : null,
        excludeTags: excludeTags.length ? excludeTags : null,
      },
    };

    setLoading(true);
    try {
      if (isEditing) {
        await axios.put(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/outreach/email-campaign/${id}`, campaignData);
      } else {
        await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/outreach/email-campaign`, campaignData);
      }

      history.push("/outreach/email");
    } catch (error) {
      console.error("Error:", error);
      alert("Failed to save campaign");
    } finally {
      setLoading(false);
    }
  };

  return {
    tags,
    campaignName,
    setCampaignName,
    subject,
    setSubject,
    emailContent,
    setEmailContent,
    sendDate,
    setSendDate,
    isFilterModalOpen,
    setIsFilterModalOpen,
    includeZipCodes,
    setIncludeZipCodes,
    excludeZipCodes,
    setExcludeZipCodes,
    includeCities,
    setIncludeCities,
    excludeCities,
    setExcludeCities,
    includeStates,
    setIncludeStates,
    excludeStates,
    setExcludeStates,
    includeTags,
    setIncludeTags,
    excludeTags,
    setExcludeTags,
    touched,
    setTouched,
    errors,
    isFormValid,
    loading,
    handleSubmit,
  };
};
